// src/apis/ApiClient.ts
import axios, {
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
  AxiosError,
  InternalAxiosRequestConfig,
} from "axios";
import { useAuthStore } from "../../store/authStore";
import { refreshAccessToken } from "apis/user/refreshAccessToken";
import { DEFAULT_HEADERS } from "./headers";

interface CustomAxiosRequestConfig extends AxiosRequestConfig {
  _retry?: boolean;
}

interface ApiErrorResponse {
  message: string;
  code?: string;
}

const SPRING_BACKEND_URL = import.meta.env.VITE_SPRING_BACKEND_URL;
const GO_BACKEND_URL = import.meta.env.VITE_GO_BACKEND_URL;
const DASHBOARD_BACKEND_URL = import.meta.env.VITE_DASHBOARD_URL;

export type BackendType = 'spring' | 'go' | 'dashboard';

export class ApiClient {
  private static instance: ApiClient;
  private springInstance: AxiosInstance;
  private goInstance: AxiosInstance;
  private dashboardInstance: AxiosInstance;
  private refreshPromise: Promise<string> | null = null;

  private constructor() {
    // Spring 백엔드용 인스턴스
    this.springInstance = axios.create({
      baseURL: SPRING_BACKEND_URL,
      timeout: 10000,
      withCredentials: true,
      headers: DEFAULT_HEADERS,
    });

    // Go 백엔드용 인스턴스  
    this.goInstance = axios.create({
      baseURL: GO_BACKEND_URL,
      timeout: 0, // SSE를 위해 무제한
      withCredentials: false,
      headers: {
        ...DEFAULT_HEADERS,
        'Accept': 'application/json',
      },
    });

    // Dashboard 백엔드용 인스턴스
    this.dashboardInstance = axios.create({
      baseURL: DASHBOARD_BACKEND_URL,
      timeout: 10000,
      withCredentials: true,
      headers: DEFAULT_HEADERS,
    });

    this.setupInterceptors();
  }

  public static getInstance(): ApiClient {
    if (!ApiClient.instance) {
      ApiClient.instance = new ApiClient();
    }
    return ApiClient.instance;
  }

  public getAxiosInstance(backend: BackendType = 'spring'): AxiosInstance {
    switch (backend) {
      case 'spring':
        return this.springInstance;
      case 'go':
        return this.goInstance;
      case 'dashboard':
        return this.dashboardInstance;
      default:
        return this.springInstance;
    }
  }

  public getSpringInstance(): AxiosInstance {
    return this.springInstance;
  }

  public getGoInstance(): AxiosInstance {
    return this.goInstance;
  }

  public getDashboardInstance(): AxiosInstance {
    return this.dashboardInstance;
  }

  /**
   * 토큰 갱신을 동기화하여 동시 요청 시 race condition을 방지
   */
  private async getRefreshedToken(): Promise<string> {
    // 이미 토큰 갱신이 진행 중이면 해당 Promise를 반환
    if (this.refreshPromise) {
      console.log("🔄 토큰 갱신이 이미 진행 중입니다. 기존 Promise를 반환합니다.");
      return this.refreshPromise;
    }

    console.log("🆕 새로운 토큰 갱신을 시작합니다.");
    
    // 새로운 토큰 갱신 시작
    this.refreshPromise = refreshAccessToken()
      .then((token) => {
        console.log("✅ 토큰 갱신 성공");
        return token;
      })
      .catch((error) => {
        console.error("❌ 토큰 갱신 실패:", error);
        throw error;
      })
      .finally(() => {
        // 토큰 갱신 완료 후 Promise 초기화
        console.log("🔚 토큰 갱신 프로세스 완료, Promise 초기화");
        this.refreshPromise = null;
      });

    return this.refreshPromise;
  }

  private setupInterceptors(): void {
    // 공통 인터셉터 설정 함수
    const setupCommonInterceptors = (instance: AxiosInstance) => {
      // 요청 인터셉터
      instance.interceptors.request.use(
        (config: InternalAxiosRequestConfig) => {
          const accessToken = useAuthStore.getState().accessToken;
          if (accessToken) {
            config.headers = config.headers || {};
            config.headers["Authorization"] = `Bearer ${accessToken}`;
          }
          return config;
        },
        (error: AxiosError) => Promise.reject(error)
      );

      // 응답 인터셉터
      instance.interceptors.response.use(
        (response: AxiosResponse) => response,
        async (error: AxiosError<ApiErrorResponse>) => {
          const originalRequest = error.config as CustomAxiosRequestConfig;

          // 토큰 리프레시 엔드포인트는 무시
          const isRefreshCall = originalRequest.url?.includes("/auth/token/refresh");

          // 401 에러 시 토큰 갱신 시도
          if (
            error.response?.status === 401 &&
            !originalRequest._retry &&
            !isRefreshCall
          ) {
            originalRequest._retry = true;
            try {
              const newAccessToken = await this.getRefreshedToken();

              // zustand 스토어에 갱신된 토큰 저장
              useAuthStore.getState().setAuth(useAuthStore.getState().user, newAccessToken);
              useAuthStore.getState().setHydrationPending(false);

              // 원래 요청 헤더에 새로운 토큰 설정
              originalRequest.headers = originalRequest.headers || {};
              originalRequest.headers["Authorization"] = `Bearer ${newAccessToken}`;

              return instance(originalRequest);
            } catch (refreshError) {
              console.error("토큰 갱신 실패:", refreshError instanceof Error ? refreshError.message : "Unknown error");
              useAuthStore.getState().logout();
              window.location.href = "/login";
              return Promise.reject(refreshError);
            }
          }

          // 403 에러 시 권한 없음 로깅
          if (error.response?.status === 403) {
            console.error("접근 권한이 없습니다:", error.response.data?.message ?? "권한이 없습니다.");
          }

          return Promise.reject(error);
        }
      );
    };

    // 모든 인스턴스에 공통 인터셉터 적용
    setupCommonInterceptors(this.springInstance);
    setupCommonInterceptors(this.goInstance);
    setupCommonInterceptors(this.dashboardInstance);
  }
}

// 기본 Spring 백엔드 클라이언트 (하위 호환성)
export const apiClient = ApiClient.getInstance().getAxiosInstance('spring');

// 백엔드별 클라이언트 접근
export const springClient = ApiClient.getInstance().getSpringInstance();
export const goClient = ApiClient.getInstance().getGoInstance();
export const dashboardClient = ApiClient.getInstance().getDashboardInstance();